import {createRoot} from 'react-dom/client';

import {initCountryRestriction} from 'app/components/countrySelection/initCountryRestriction';
import {initCmpModalFocus} from 'app/modules/cmpConsent/initCmpModalFocus';
import {setLazyLoadPageAction} from 'app/modules/lazyLoadPage/store/actions';
import {initLoadCptSubPage} from 'app/modules/loadCptSubPage/initLoadCptSubPage';
import {initJSModal} from 'app/modules/modal';
import {initSetHref} from 'app/modules/setHref';
import {getGemSettings} from 'app/utils/config';
import {type GemSettings} from 'app/utils/config/interface';
import {initDevelopmentShortcuts} from 'app/utils/developmentShortcuts/initDevelopmentShortcuts';
import {GemInitializeWithWebComponents} from 'app/utils/initialize/components/gemInitializeWithWebComponents';
import {getLazyLoadPage} from 'app/utils/initialize/getLazyLoadPage';
import {hydrateComponents, hydrateWebComponents} from 'app/utils/initialize/hydrateComponents';
import {initKeyboardOrMouse} from 'app/utils/initialize/initKeyboardOrMouse';
import {initSmoothPageScroll} from 'app/utils/smoothPageScroll';
import {initHashHistory} from 'app/utils/urlState/initHashHistory';
import {initJSI} from 'JSI/initJSI';
import {initServiceWorker} from 'sw/install';

import {initPrintingHandler} from './modules/printingHandler';
import {injectSvgImages} from './utils/image/injectSVG';
import {initMediaListeners} from './utils/initialize/addMediaListener';
import {initPageShow} from './utils/initialize/initPageShow';
import {initPreferredInstitute} from './utils/initialize/initPreferredInstitute';
import {initPublicPath} from './utils/initialize/initPublicPath';
import {initReinitializeListener} from './utils/initialize/initReinitializeListener';
import {redirectByOperatingSystem} from './utils/redirectByOperatingSystem';

async function start() {
    const gemSettings = getGemSettings();

    redirectByOperatingSystem(gemSettings);

    // GEM public namespace
    window.GEM = {settings: gemSettings};

    syncInit(gemSettings);

    await asyncInit(gemSettings);

    const lazyLoadPage = getLazyLoadPage(gemSettings);
    setLazyLoadPageAction(lazyLoadPage);

    // Manage SSR
    hydrateComponents(gemSettings, lazyLoadPage);
    hydrateWebComponents(gemSettings);

    // Manage client-side rendering
    const reactClientRoot = document.getElementById('__rcr__');
    if (reactClientRoot) {
        const root = createRoot(reactClientRoot);
        root.render(<GemInitializeWithWebComponents gemSettings={gemSettings} context={document.documentElement} lazyLoadPage={lazyLoadPage} />);
    }
}

/**
 * Initialize all the necessary NON-ASYNC functions.
 * @param gemSettings
 */
function syncInit(gemSettings: GemSettings) {
    [
        initPublicPath(gemSettings),
        initDevelopmentShortcuts,
        initHashHistory,
        initPageShow,
        initSetHref,
        initKeyboardOrMouse,
        initServiceWorker(gemSettings),
        initSmoothPageScroll,
        initMediaListeners,
        initJSModal(gemSettings),
        initPrintingHandler,
        initJSI(gemSettings),
        initCmpModalFocus,
        initReinitializeListener(gemSettings),
        injectSvgImages,
        initCountryRestriction(gemSettings),
        initPreferredInstitute(gemSettings),
    ].forEach((initFn) => {
        try {
            initFn();
        } catch (err) {
            console.error(err);
        }
    });
}

/**
 * Initialize all the necessary ASYNC functions.
 * @param gemSettings
 */
async function asyncInit(gemSettings: GemSettings) {
    const results = await Promise.allSettled([initLoadCptSubPage(gemSettings)]);

    results.forEach((result) => {
        if (result.status === 'rejected') {
            console.error(result.reason);
        }
    });
}

start();
